import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Mic, History, BookOpen, Zap, NotebookPen, CreditCard, Settings, LogOut, ShieldCheck, Menu, X, Keyboard, LineChart, Users } from "lucide-react";
import { useAuth } from "@/lib/auth.jsx";
import { LOGO_COLOR } from "@/lib/brand";
import AnnouncementBanner from "@/components/AnnouncementBanner.jsx";
import MobileBottomNav from "@/components/app/MobileBottomNav.jsx";
import NotificationBell from "@/components/NotificationBell.jsx";
import STTStatusBanner from "@/components/STTStatusBanner.jsx";

const links = [
  { to: "/app", icon: Mic, label: "Voice Studio", end: true, testid: "nav-voice-studio" },
  { to: "/app/notes", icon: Users, label: "Notes Taker", testid: "nav-notes" },
  { to: "/app/history", icon: History, label: "History", testid: "nav-history" },
  { to: "/app/insights", icon: LineChart, label: "Insights", testid: "nav-insights" },
  { to: "/app/dictionary", icon: BookOpen, label: "Dictionary", testid: "nav-dictionary" },
  { to: "/app/snippets", icon: Zap, label: "Snippets", testid: "nav-snippets" },
  { to: "/app/draftpad", icon: NotebookPen, label: "Draftpad", testid: "nav-draftpad" },
  { to: "/app/shortcuts", icon: Keyboard, label: "Shortcuts & Apps", testid: "nav-shortcuts" },
  { to: "/app/billing", icon: CreditCard, label: "Billing", testid: "nav-billing" },
  { to: "/app/settings", icon: Settings, label: "Settings", testid: "nav-settings" },
];

function UsagePanel({ user, onUpgrade }) {
  const pct = user ? Math.min(100, Math.round((user.words_used / Math.max(user.words_limit, 1)) * 100)) : 0;
  return (
    <div className="p-4 m-3 rounded-2xl bg-gradient-to-br from-[#F4F2FF] to-[#EAF4FF] border border-brand-borderc">
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-brand-navy capitalize">{user?.plan} plan</span>
        <span className="text-brand-muted">{pct}%</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
        <div className="h-full bg-gradient-primary" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-xs text-brand-muted">
        {user?.words_used?.toLocaleString()} / {user?.words_limit?.toLocaleString()} words
      </div>
      {user?.plan === "free" && (
        <button onClick={onUpgrade} className="mt-3 w-full text-xs btn-primary-gradient rounded-full py-2 font-semibold">
          Upgrade →
        </button>
      )}
    </div>
  );
}

function SidebarBody({ user, onLogout, navigate, onItemClick }) {
  return (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {links.map((l) => (
          <NavLink
            key={l.to} to={l.to} end={l.end} onClick={onItemClick}
            data-testid={l.testid}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
              isActive ? "bg-gradient-primary text-white shadow-glow-purple" : "text-brand-text hover:bg-brand-surface"
            }`}
          >
            <l.icon className="w-4 h-4" />
            {l.label}
          </NavLink>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/admin" onClick={onItemClick} data-testid="nav-admin" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-brand-text hover:bg-brand-surface mt-4 border-t border-brand-borderc pt-4">
            <ShieldCheck className="w-4 h-4 text-brand-pink" /> Admin Panel
          </NavLink>
        )}
      </nav>
      <UsagePanel user={user} onUpgrade={() => { onItemClick?.(); navigate("/app/billing"); }} />
      <div className="p-3 border-t border-brand-borderc">
        <button data-testid="logout-btn" onClick={onLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-brand-text hover:bg-brand-surface">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>
    </>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const onLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-brand-bg flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-brand-borderc bg-white sticky top-0 h-screen">
        <div className="p-5 border-b border-brand-borderc flex items-center justify-between gap-2">
          <NavLink to="/" className="flex items-center gap-2 min-w-0">
            <img src={LOGO_COLOR} alt="DraftVoice" className="w-9 h-9 rounded-lg bg-brand-navy shrink-0" />
            <span className="font-bold text-brand-navy truncate">Draft<span className="text-gradient-primary">Voice</span></span>
          </NavLink>
          <NotificationBell />
        </div>
        <SidebarBody user={user} onLogout={onLogout} navigate={navigate} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-brand-borderc flex items-center justify-between px-4 h-14 pt-[env(safe-area-inset-top)]">
        <button data-testid="mobile-menu-toggle" onClick={() => setDrawerOpen(true)} className="p-2 -ml-2 text-brand-navy">
          <Menu className="w-6 h-6" />
        </button>
        <NavLink to="/app" className="flex items-center gap-2">
          <img src={LOGO_COLOR} className="w-8 h-8 rounded-lg bg-brand-navy" alt="" />
          <span className="font-bold text-brand-navy">DraftVoice</span>
        </NavLink>
        <div className="flex items-center -mr-2">
          <NotificationBell />
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div data-testid="mobile-drawer-overlay" className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside data-testid="mobile-drawer" className="lg:hidden fixed top-0 left-0 bottom-0 w-72 z-50 bg-white flex flex-col animate-in slide-in-from-left duration-200">
            <div className="p-5 border-b border-brand-borderc flex items-center justify-between">
              <NavLink to="/" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2">
                <img src={LOGO_COLOR} alt="DraftVoice" className="w-9 h-9 rounded-lg bg-brand-navy" />
                <span className="font-bold text-brand-navy">Draft<span className="text-gradient-primary">Voice</span></span>
              </NavLink>
              <button onClick={() => setDrawerOpen(false)} className="p-1.5 hover:bg-brand-surface rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarBody user={user} onLogout={() => { setDrawerOpen(false); onLogout(); }} navigate={navigate} onItemClick={() => setDrawerOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 lg:pl-0 overflow-x-hidden">
        <AnnouncementBanner />
        <STTStatusBanner />
        {/* Spacer for fixed mobile top bar */}
        <div className="lg:hidden h-14" />
        <div className="pb-20 lg:pb-0">
          <Outlet />
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
}
